import { Voucher, VoucherStatus, voucherTypeColors, voucherTypeIcons } from '../../../mocks/vouchers';
import { agencyInfo } from '../../../mocks/dashboard';

const STATUS_STYLES: Record<VoucherStatus, string> = {
  Active: 'bg-emerald-100 text-emerald-700',
  Used: 'bg-gray-100 text-gray-500',
  Cancelled: 'bg-red-100 text-red-600',
  Expired: 'bg-orange-100 text-orange-600',
};

interface VoucherCardProps {
  voucher: Voucher;
  onView: (voucher: Voucher) => void;
}

export default function VoucherCard({ voucher, onView }: VoucherCardProps) {
  const brand = agencyInfo.brandColor;
  const typeColor = voucherTypeColors[voucher.type];
  const typeIcon = voucherTypeIcons[voucher.type];

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div
      onClick={() => onView(voucher)}
      className={`bg-white border rounded-2xl overflow-hidden cursor-pointer hover:border-gray-200 transition-all group ${
        voucher.status === 'Used' ? 'opacity-70' : 'hover:translate-y-[-2px]'
      } border-gray-100`}
    >
      {/* Color bar top */}
      <div className="h-1.5 w-full" style={{ backgroundColor: typeColor }} />

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ backgroundColor: `${typeColor}15` }}>
              <i className={`${typeIcon} text-lg`} style={{ color: typeColor }} />
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{voucher.type} Voucher</p>
              <p className="font-bold text-gray-800 text-sm">{voucher.number}</p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[voucher.status]}`}>
            {voucher.status}
          </span>
        </div>

        {/* Service Title */}
        <p className="text-sm font-semibold text-gray-800 mb-1 line-clamp-2 leading-snug">{voucher.serviceTitle}</p>
        <p className="text-xs text-gray-400 mb-4">{voucher.supplierName}</p>

        {/* Details row */}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="text-gray-400 mb-0.5">Customer</p>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0" style={{ backgroundColor: brand }}>
                {voucher.customerAvatar}
              </div>
              <span className="font-medium text-gray-700 truncate">{voucher.customer}</span>
            </div>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Destination</p>
            <p className="font-medium text-gray-700 truncate">{voucher.destination}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Travel Date</p>
            <p className="font-medium text-gray-700">{fmtDate(voucher.travelDate)}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5">Pax</p>
            <p className="font-medium text-gray-700">{voucher.pax} {voucher.pax === 1 ? 'person' : 'persons'}</p>
          </div>
        </div>

        {/* Booking + Confirmation */}
        <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {voucher.bookingId && (
              <span className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-500 font-medium">{voucher.bookingId}</span>
            )}
            <span className="text-xs text-gray-400 font-mono">{voucher.confirmationCode}</span>
          </div>
          <button
            className="flex items-center gap-1 text-xs font-semibold cursor-pointer group-hover:opacity-80"
            style={{ color: brand }}
            onClick={(e) => { e.stopPropagation(); onView(voucher); }}
          >
            View
            <i className="ri-arrow-right-line text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}
